import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { MdDelete, MdEdit, MdPreview } from "react-icons/md";
import "./productList.css";
import { useUserAuth } from "../../context/UserAuthContext";
import axios from "axios";
import { NumericFormat } from "react-number-format";

const ProductList = () => {
  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;
  const { user, language, token, currency } = useUserAuth();
  const merchant_id = user.uid;

  const [data, setData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);

  const fetchData = useCallback(async () => {
    if (!merchant_id || !token) return;

    try {
      const { data: response } = await axios.get(
        `${base_url}/api/v1/products/vendor-product/${merchant_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setData(response?.data ?? []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    }
  }, [merchant_id, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStockToggle = async (e, id) => {
    const newStockStatus = e.target.checked;
    try {
      await axios.put(
        `${base_url}/api/v1/products/stock/${id}`,
        { stock: newStockStatus },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      toast.success("Product stock updated successfully");
      setData((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, stock: newStockStatus } : item,
        ),
      );
    } catch (err) {
      console.error("Error:", err);
      toast.error("Failed to update stock status");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete?")) return;

    try {
      await axios.delete(`${base_url}/api/v1/products/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success("Product deleted successfully");
      setData(data.filter((item) => item.id !== id));
    } catch (err) {
      console.error("Error deleting product:", err);
      toast.error("Failed to delete product");
    }
  };

  const filteredProducts = data?.filter((product) =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const makeProfilePictureHandler = async (image_id, product_id) => {
    const choice = window.confirm(
      "Are you sure you want to make this image primary?",
    );
    if (!choice) return;
    try {
      const response = await axios.put(
        `${base_url}/api/v1/products/product-image/set-primary`,
        {
          image_id,
          product_id,
        },
      );

      if (response?.data?.success) {
        setCurrentProduct((prev) => {
          if (!prev) return prev;

          const updatedImages = prev.images.map((img) => ({
            ...img,
            is_primary: img.id === image_id,
          }));
          updatedImages.sort((a, b) => b.is_primary - a.is_primary);
          return {
            ...prev,
            images: updatedImages,
          };
        });
        setData((prevData) =>
          prevData.map((product) =>
            product.id === product_id
              ? {
                  ...product,
                  images: product.images
                    .map((img) => ({
                      ...img,
                      is_primary: img.id === image_id,
                    }))
                    .sort((a, b) => b.is_primary - a.is_primary),
                }
              : product,
          ),
        );
        toast.success("Image set as primary successfully");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error setting primary image");
    }
  };
  return (
    <div className="page-wrapper">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div
                className="card-header text-white"
                style={{ backgroundColor: "#116ba8" }}
              >
                <div className="row align-items-center w-100">
                  <div className="col-md-6 mb-2 mb-md-0">
                    <input
                      type="text"
                      placeholder={
                        language === "en"
                          ? "Search Product..."
                          : "उत्पाद खोजें..."
                      }
                      className="form-control"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="col-md-6 text-md-end text-center">
                    <h4 className="mb-0">
                      {language === "en"
                        ? "Product List"
                        : "प्रोडक्ट और सर्विस लिस्ट"}
                    </h4>
                  </div>
                </div>
              </div>
              <div className="card-body">
                {filteredProducts?.length > 0 ? (
                  <div className="row">
                    {filteredProducts?.map((product) => (
                      <div
                        className="col-12 col-md-12 col-lg-4 mb-4"
                        key={product.id}
                      >
                        <div className="card h-100 luxury-product-card">
                          <div className="luxury-img-wrap">
                            <img
                              src={product.images?.[0]?.desktop_image}
                              alt={product.name}
                              className="luxury-product-img"
                            />
                            <div className="luxury-overlay"></div>
                            <div className="luxury-actions">
                              <MdPreview
                                onClick={() => {
                                  setCurrentProduct(product);
                                  setShowModal(true);
                                }}
                              />
                              <Link
                                to={`/dashboard/edit-product/${product.id}`}
                              >
                                <MdEdit />
                              </Link>
                              <MdDelete
                                onClick={() => handleDelete(product.id)}
                              />
                            </div>

                            <span
                              className={`luxury-stock ${product.stock ? "in" : "out"}`}
                            >
                              {product.stock ? "Available" : "Out of Stock"}
                            </span>
                            <div className="luxury-body">
                              <h1>{product.name}</h1>
                              <div className="luxury-footer">
                                <div className="price-section">
                                  <span className="price">
                                    {currency.symbol}
                                    &nbsp;
                                    {Math.floor(
                                      currency.value * product.discount_price,
                                    )}
                                  </span>
                                  <span className="old-price">
                                    {currency.symbol}
                                    &nbsp;
                                    {Math.floor(currency.value * product.price)}
                                  </span>
                                </div>
                                <div>
                                  <div className="form-check form-switch">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      checked={product.stock}
                                      onChange={(e) =>
                                        handleStockToggle(e, product.id)
                                      }
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-muted">
                      {language === "en"
                        ? "Showcase better products and services to make it easier for customers to understand your business"
                        : "बेहतर प्रोडक्ट और सर्विसेस दिखाएँ, जिससे कस्टमर आपके बिज़नेस को आसानी से समझ सकें"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Product Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentProduct && (
            <div className="product-details">
              <div className="row mb-3">
                <div className="col-md-6">
                  <h6>Name:</h6>
                  <p>{currentProduct.name}</p>
                </div>
                <div className="col-md-6">
                  <h6>Price:</h6>
                  <p>
                    <span className="text-danger">
                      {" "}
                      <NumericFormat
                        value={Math.floor(
                          currency.value * currentProduct?.discount_price,
                        )}
                        displayType="text"
                        thousandsGroupStyle={
                          currency.symbol === "₹" ? "lakh" : "thousand"
                        }
                        thousandSeparator=","
                        renderText={(formattedValue) => (
                          <span>
                            {currency.symbol}&nbsp;{formattedValue}
                          </span>
                        )}
                      />
                    </span>
                    <span className="text-muted ms-2">
                      <s>
                        <NumericFormat
                          value={Math.floor(
                            currency.value * currentProduct?.price,
                          )}
                          displayType="text"
                          thousandsGroupStyle={
                            currency.symbol === "₹" ? "lakh" : "thousand"
                          }
                          thousandSeparator=","
                          renderText={(formattedValue) => (
                            <span>
                              {currency.symbol}&nbsp;{formattedValue}
                            </span>
                          )}
                        />
                      </s>
                    </span>
                  </p>
                </div>
              </div>

              <div className="mb-3">
                <h6>Description:</h6>
                <div
                  className="product-description"
                  dangerouslySetInnerHTML={{
                    __html: currentProduct.product_description,
                  }}
                />
              </div>
              <div className="product-images-section">
                <h6 className="mb-2">Images</h6>
                <div className="product-images-grid">
                  {currentProduct.images?.map((img) => (
                    <div
                      key={img.id}
                      className={`image-card ${img.is_primary ? "primary" : ""}`}
                    >
                      <img
                        src={img.mobile_image}
                        alt="product"
                        loading="lazy"
                      />
                      {currentProduct?.images?.length > 1 &&
                        (img.is_primary ? (
                          <span className="primary-badge">Primary</span>
                        ) : (
                          <button
                            className="make-primary-btn"
                            onClick={() =>
                              makeProfilePictureHandler(img.id, img.product_id)
                            }
                          >
                            Make Primary
                          </button>
                        ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ProductList;
